import React from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CompanyResultCard from './CompanyResultCard';
import ChatArtifactAnswerEvidence from './ChatArtifactAnswerEvidence';
import ChatMessageSuggestions from './ChatMessageSuggestions';
import ChatClarificationOptions from './ChatClarificationOptions';
import ChatResultArtifact from './ChatResultArtifact';
import ChatOwnerProgress from './ChatOwnerProgress';

export default function AssistantMessage({
  content,
  notice = null,
  companies = [],
  artifactAnswer = null,
  artifactAnswerActions = [],
  suggestions = [],
  clarificationOptions = [],
  onSelectClarificationOption,
  artifactDisplay = null,
  isLatestList = false,
  artifactActionInFlightKey = null,
  isStreaming = false,
  refinementSummary = null,
  progressLabel = null,
  ownerProgress = null,
  ownerProgressStatus = null,
  ownerProgressRefreshError = null,
  showExport = false,
  exportDisabled = false,
  isExporting = false,
  exportError = null,
  exportWarning = null,
  onExportCompanies,
  onRunArtifactAnswerAction,
  artifactActionNotice = null,
  artifactActionNoticeSeverity = 'success',
  artifactActionError = null,
  workspace = null,
  onRunArtifactAction,
  onSendSuggestion,
  suggestionsDisabled = false,
  visualVariant = 'legacy',
}) {
  const hasArtifactDisplay = Boolean(artifactDisplay);
  const isModern = visualVariant === 'modern';
  const shellMaxWidth = isModern ? '760px' : '860px';

  const contentShell = (
    <>
        {refinementSummary && (
          <Box
            data-testid="refinement-summary"
            sx={{
              borderLeft: '3px solid',
              borderLeftColor: 'primary.main',
              pl: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 0 }}
            >
              {refinementSummary}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.65 }}>
          {content}
          {isStreaming && (
            <Box
              component="span"
              data-testid="streaming-cursor"
              sx={{
                display: 'inline-block',
                width: '0.5em',
                height: '1em',
                bgcolor: isModern ? 'primary.main' : 'text.primary',
                ml: '2px',
                verticalAlign: 'text-bottom',
                '@keyframes chatBlink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0 },
                },
                animation: 'chatBlink 1s step-end infinite',
              }}
            />
          )}
        </Typography>
        {progressLabel && (
          <Typography role="status" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {progressLabel}
          </Typography>
        )}
        {notice && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {notice}
          </Typography>
        )}
    </>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      width="100%"
      maxWidth={shellMaxWidth}
      mb={1.5}
    >
      {isModern ? (
        <Box
          data-testid="assistant-message-modern"
          sx={{
            width: '100%',
            maxWidth: 'min(760px, 100%)',
            boxSizing: 'border-box',
            py: 0.25,
          }}
        >
          {contentShell}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            maxWidth: 'min(760px, 100%)',
            boxSizing: 'border-box',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '18px 18px 18px 4px',
            bgcolor: 'background.paper',
          }}
        >
          {contentShell}
        </Paper>
      )}

      <ChatArtifactAnswerEvidence
        answer={artifactAnswer}
        actions={artifactAnswerActions}
        actionInFlightKey={artifactActionInFlightKey}
        onRunAction={onRunArtifactAnswerAction}
      />
      <ChatOwnerProgress progress={ownerProgress} status={ownerProgressStatus} refreshError={ownerProgressRefreshError} />

      {!hasArtifactDisplay && artifactActionNotice && (
        <Alert severity={artifactActionNoticeSeverity} sx={{ mt: 1, py: 0.5 }}>
          {artifactActionNotice}
        </Alert>
      )}

      {!hasArtifactDisplay && artifactActionError && (
        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
          {artifactActionError}
        </Alert>
      )}

      {hasArtifactDisplay && (
        <ChatResultArtifact
          artifact={artifactDisplay}
          isLatestList={isLatestList}
          isExporting={isExporting}
          exportError={exportError}
          exportWarning={exportWarning}
          actionNotice={artifactActionNotice}
          actionNoticeSeverity={artifactActionNoticeSeverity}
          actionError={artifactActionError}
          workspace={workspace}
          actionInFlightKey={artifactActionInFlightKey}
          onRunAction={onRunArtifactAction}
        />
      )}

      {!hasArtifactDisplay && showExport && (
        <Box mt={1} width="100%" maxWidth="100%">
          <Box display="flex" alignItems="center" gap={1} mb={companies.length > 0 ? 1 : 0}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              disabled={exportDisabled || isExporting}
              onClick={onExportCompanies}
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Box>
          {exportError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
              {exportError}
            </Typography>
          )}
          {exportWarning?.message && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: 'block', mb: 1 }}
            >
              {exportWarning.message}
            </Typography>
          )}
        </Box>
      )}

      {!hasArtifactDisplay && companies.length > 0 && (
        <Box
          display="flex"
          gap={1.5}
          mt={showExport ? 0 : 1}
          pb={0.5}
          sx={{ overflowX: 'auto', maxWidth: '100%' }}
        >
          {companies.map((company, i) => (
            <CompanyResultCard key={company.id ?? i} {...company} />
          ))}
        </Box>
      )}

      <ChatClarificationOptions
        options={clarificationOptions}
        disabled={suggestionsDisabled}
        onSelect={onSelectClarificationOption}
      />

      <ChatMessageSuggestions
        suggestions={suggestions}
        disabled={suggestionsDisabled}
        onSendSuggestion={onSendSuggestion}
      />
    </Box>
  );
}
